import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate, formatDisplayDateTime } from "../utils/dateFormat";
import { 
  Calendar, LogOut, ChevronLeft, ChevronRight, X, FileText, Eye, RefreshCw, CheckCircle, AlertCircle, Clock, Users, Baby, User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth hook for cleaning users
function useCleaningAuth(externalToken = null, isImpersonation = false) {
  const navigate = useNavigate();
  // If external token provided (impersonation mode), use it
  // Otherwise use localStorage tokens
  const [token, setToken] = useState(
    externalToken || localStorage.getItem("cleaning_token") || localStorage.getItem("token")
  );
  const [user, setUser] = useState(() => {
    if (isImpersonation) {
      return { role: "cleaning", impersonated: true };
    }
    try {
      const cleaningUser = localStorage.getItem("cleaning_user");
      if (cleaningUser) return JSON.parse(cleaningUser);
      // Fallback al vecchio formato
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    // Skip navigation checks in impersonation mode
    if (isImpersonation) return;
    
    if (!token) {
      navigate("/admin");
      return;
    }
    if (user.role && user.role !== "cleaning") {
      navigate("/admin/dashboard");
      return;
    }
  }, [token, user.role, navigate, isImpersonation]);

  const logout = () => {
    // In impersonation mode, just close the tab
    if (isImpersonation) {
      window.close();
      return;
    }
    // Rimuovi token Cleaning
    localStorage.removeItem("cleaning_token");
    localStorage.removeItem("cleaning_user");
    // Rimuovi anche vecchi token generici
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    navigate("/admin");
  };

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });
  
  return { token, user, logout, getAuthHeaders, isImpersonation };
}

// Helper functions - use centralized date formatting
const formatDateLocal = (dateStr) => {
  return formatDisplayDate(dateStr);
};

const formatSyncTimestamp = (isoString) => {
  return formatDisplayDateTime(isoString);
};

// Format children ages as (8Y) (9Y)
const formatChildrenAges = (childrenAges) => {
  if (!childrenAges) return "";
  // childrenAges can be string like "8, 9" or "8,9" or array
  const ages = typeof childrenAges === 'string' 
    ? childrenAges.split(/[,\s]+/).filter(a => a.trim())
    : (Array.isArray(childrenAges) ? childrenAges : []);
  return ages.map(age => `(${age}Y)`).join(' ');
};

const monthNames = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const date_to_str = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function CleaningDashboard({ 
  impersonateToken = null, 
  tokenExpiry = null,
  timeRemaining = null,
  formatTimeRemaining = null,
  getCountdownColor = null,
  onTokenExpired = null,
  onRenewToken = null,
  renewingToken = false
}) {
  const isImpersonation = !!impersonateToken;
  const { user, logout, getAuthHeaders } = useCleaningAuth(impersonateToken, isImpersonation);
  
  // Stati per dati calendario
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [calendarBlocked, setCalendarBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Stato sincronizzazione
  const [syncStatus, setSyncStatus] = useState({
    synced: false,
    last_sync: null,
    sync_status: "loading",
    sync_error: null
  });
  
  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Modal per dettagli prenotazione
  const [bookingModal, setBookingModal] = useState({ open: false, booking: null });
  
  // Expanded notes in cards
  const [expandedNotes, setExpandedNotes] = useState({});

  // Smart polling
  const [lastKnownVersion, setLastKnownVersion] = useState(null);

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cleaning/sync-status`, getAuthHeaders());
      setSyncStatus(res.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
      setSyncStatus(prev => ({
        ...prev,
        sync_status: "error",
        sync_error: "Impossibile recuperare lo stato di sincronizzazione"
      }));
    }
  }, [getAuthHeaders]);  

  // Fetch calendar data
  const fetchCalendarData = useCallback(async (showToast = false) => {
    try {
      const res = await axios.get(`${API}/cleaning/calendar-data`, getAuthHeaders());
      setCalendarBookings(res.data.bookings || []);
      setCalendarBlocked(res.data.blocked_dates || []);
      if (showToast) {
        toast.success("Dati aggiornati");
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        // In impersonation mode, notify parent of token expiry
        if (isImpersonation && onTokenExpired) {
          onTokenExpired();
        } else {
          logout();
        }
      }
      if (showToast) {
        toast.error("Errore nell'aggiornamento");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual refresh
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchCalendarData(true),
      fetchSyncStatus()
    ]);
  };

  // Smart polling: check version every 60 seconds
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await axios.get(`${API}/calendar/version`, getAuthHeaders());
        const serverVersion = res.data.version;
        
        if (lastKnownVersion !== null && serverVersion !== lastKnownVersion) {
          console.log(`📊 Cleaning Dashboard: Calendar version changed (${lastKnownVersion} → ${serverVersion}), refreshing data...`);
          fetchCalendarData();
          fetchSyncStatus();
        }
        setLastKnownVersion(serverVersion);
      } catch (error) {
        console.error("Error checking calendar version:", error);
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, 60000);
    return () => clearInterval(interval);
  }, [lastKnownVersion, fetchCalendarData, fetchSyncStatus, getAuthHeaders]);

  // Initial load
  useEffect(() => {
    fetchCalendarData();
    fetchSyncStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calendar navigation
  const goToPrevMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const goToToday = () => {
    const now = new Date();
    setCalendarMonth({ year: now.getFullYear(), month: now.getMonth() });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(calendarMonth.year, calendarMonth.month, 1);
    const lastDay = new Date(calendarMonth.year, calendarMonth.month + 1, 0);
    const startDay = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push({ day: null, date: null });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, date: dateStr });
    }
    return days;
  };

  // Helper functions for calendar
  const isDateInBooking = (dateStr, booking) => {
    if (!booking.check_in || !booking.check_out) return false;
    return dateStr >= booking.check_in && dateStr < booking.check_out;
  };

  // Helper: blocco manuale Admin (manutenzione, chiusure) - SOLO manual e hold, NO booking
  // "source: booking" = blocco auto-generato da prenotazione (NON è CHIUSO)
  // "source: manual" o "source: hold" = blocco manuale admin (CHIUSO)
  const getManualBlock = (dateStr) => {
    return calendarBlocked.find(b => 
      b.date === dateStr && 
      (b.source === "manual" || b.source === "hold")
    );
  };

  const getBookingForDate = (dateStr) => {
    return calendarBookings.find(b => 
      (b.status === "confirmed" || b.status === "paid" || b.source?.startsWith("ical")) &&
      isDateInBooking(dateStr, b)
    );
  };

  // Helper: blocco iCal (prenotazione reale da piattaforme esterne)
  const getIcalBlock = (dateStr) => {
    return calendarBlocked.find(b => 
      b.date === dateStr && 
      (b.source === "ical" || b.source?.startsWith("ical_"))
    );
  };

  // Check-out SOLO sulla data ESATTA di check_out (dateStr === booking.check_out)
  // Il triangolo appare SEMPRE sulla data di checkout, anche se la cella è "CHIUSO"
  // Include: confirmed, paid, ical imports (source === "ical" o status === "ical")
  const isCheckOutDay = (dateStr) => {
    return calendarBookings.some(b => 
      b.check_out === dateStr && // ESATTAMENTE la data di checkout
      (b.status === "confirmed" || b.status === "paid" || b.status === "ical" || b.source?.startsWith("ical") || b.source === "ical")
    );
  };

  const todayStr = date_to_str(new Date());
  const isToday = (dateStr) => dateStr === todayStr;
  const isPast = (dateStr) => dateStr < todayStr;

  // Calculate next events with full booking data
  // NOTA: Mostra TUTTI gli eventi ordinati cronologicamente
  // Se c'è overbooking (due IN consecutivi), viene evidenziato visivamente
  const getNextEvents = () => {
    const today = new Date();
    const todayStr = date_to_str(today);
    const endDate = date_to_str(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));

    // Filtra solo prenotazioni valide (confirmed, paid, ical)
    const validBookings = calendarBookings.filter(b => 
      (b.status === "confirmed" || b.status === "paid" || b.source?.startsWith("ical") || b.source === "ical") &&
      b.check_in && b.check_out
    );

    // Genera tutti gli eventi
    const allEvents = [];
    
    validBookings.forEach((b) => {
      const isIcal = b.source?.startsWith("ical") || b.source === "ical";
      const eventData = {
        name: isIcal ? (b.platform_name || "Airbnb") : (b.name || b.customer_name || "Ospite"),
        guests: (b.adults || 0) + (b.children || 0) + (b.infants || 0),
        adults: b.adults || 0,
        children: b.children || 0,
        children_ages: b.children_ages || "",
        infants: b.infants || 0,
        nights: Math.ceil((new Date(b.check_out) - new Date(b.check_in)) / (1000 * 60 * 60 * 24)),
        source: isIcal ? "ical" : (b.source || "website"),
        notes: b.notes || b.special_requests || "",
        booking: b
      };

      // Aggiungi CHECK-IN se nel range futuro
      if (b.check_in >= todayStr && b.check_in <= endDate) {
        allEvents.push({
          ...eventData,
          type: "check_in",
          date: b.check_in
        });
      }

      // Aggiungi CHECK-OUT se nel range futuro
      if (b.check_out >= todayStr && b.check_out <= endDate) {
        allEvents.push({
          ...eventData,
          type: "check_out",
          date: b.check_out
        });
      }
    });

    // Ordinamento: per data, poi checkout prima di checkin (stessa data)
    allEvents.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      // Stessa data: checkout sempre prima di checkin
      if (a.type !== b.type) {
        return a.type === "check_out" ? -1 : 1;
      }
      return 0;
    });

    // Marca gli eventi che indicano OVERBOOKING (due IN o due OUT consecutivi)
    const result = allEvents.slice(0, 4).map((event, idx, arr) => {
      let isOverbooking = false;
      
      if (idx > 0) {
        const prevEvent = arr[idx - 1];
        // Due CHECK-IN consecutivi = OVERBOOKING
        if (event.type === "check_in" && prevEvent.type === "check_in") {
          isOverbooking = true;
        }
        // Due CHECK-OUT consecutivi = possibile OVERBOOKING (prenotazioni sovrapposte)
        if (event.type === "check_out" && prevEvent.type === "check_out") {
          isOverbooking = true;
        }
      }
      
      return { ...event, isOverbooking };
    });

    return result;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9F8F6" }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();
  const nextEvents = getNextEvents();

  // Sync status display
  // NOTA: Mostra lo stato dei dati del Booking, NON avvia sync iCal
  const getSyncStatusDisplay = () => {
    if (syncStatus.sync_status === "loading") {
      return { icon: Clock, color: "text-gray-500", bg: "bg-gray-100", text: "Caricamento..." };
    }
    if (syncStatus.sync_status === "success" || syncStatus.synced) {
      return { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50", text: "Booking calendar synced" };
    }
    if (syncStatus.sync_status === "error") {
      return { icon: AlertCircle, color: "text-red-600", bg: "bg-red-50", text: "Errore sync" };
    }
    if (syncStatus.sync_status === "not_configured") {
      return { icon: AlertCircle, color: "text-yellow-600", bg: "bg-yellow-50", text: "Non configurato" };
    }
    return { icon: Clock, color: "text-gray-500", bg: "bg-gray-100", text: "In attesa" };
  };

  const syncDisplay = getSyncStatusDisplay();
  const SyncIcon = syncDisplay.icon;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F8F6" }}>
      {/* Impersonation Banner - only visible when admin is viewing as cleaning */}
      {isImpersonation && (
        <div className="bg-amber-500 text-white px-4 py-2 text-sm">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>
                <strong>Modalità visualizzazione Pulizie</strong> — Stai vedendo la dashboard come la vede l'utente Pulizie
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Expiry Warning Modal - appears 1 minute before expiration */}
      {isImpersonation && timeRemaining !== null && timeRemaining <= 60 && timeRemaining > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-500 px-6 py-4 text-white">
              <div className="flex items-center gap-3">
                <Clock size={24} />
                <h3 className="text-lg font-semibold">Sessione in scadenza</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-[#44403C] mb-4">
                La sessione di visualizzazione Pulizie scade tra:
              </p>
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-amber-600">
                  {formatTimeRemaining ? formatTimeRemaining(timeRemaining) : `0:${timeRemaining.toString().padStart(2, '0')}`}
                </span>
              </div>
              <p className="text-sm text-[#78716C] mb-6">
                Clicca "Rinnova" per estendere la sessione di altri 15 minuti.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onRenewToken}
                  disabled={renewingToken}
                  className="flex-1 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8972E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {renewingToken ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Rinnovo...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Rinnova sessione
                    </>
                  )}
                </button>
                <button
                  onClick={() => window.close()}
                  className="px-4 py-2 bg-[#E7E5E4] text-[#44403C] rounded-lg hover:bg-[#D6D3D1] transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-[#1C1917] text-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar size={24} className="text-[#D4AF37]" />
            <div>
              <h1 className="font-heading text-xl">Dashboard Pulizie</h1>
              <p className="text-xs text-white/60">{isImpersonation ? "Vista Admin" : user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-white/70 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" title="Vedi sito">
              <Eye size={20} />
            </a>
            <button onClick={logout} className="text-white/70 hover:text-white transition-colors" title={isImpersonation ? "Chiudi" : "Logout"}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        
        {/* 1. Stato Sincronizzazione + Pulsante Aggiorna */}
        <div className={`${syncDisplay.bg} rounded-xl border border-[#E7E5E4] p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
          <div className="flex items-center gap-3">
            <SyncIcon size={20} className={syncDisplay.color} />
            <div>
              <p className={`font-medium ${syncDisplay.color}`}>{syncDisplay.text}</p>
              <p className="text-xs text-[#78716C]">
                Last sync: {formatSyncTimestamp(syncStatus.last_sync)}
              </p>
              {syncStatus.sync_error && syncStatus.sync_status === "error" && (
                <p className="text-xs text-red-600 mt-1">{syncStatus.sync_error}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[#1C1917] text-white rounded-lg hover:bg-[#292524] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            <span className="text-sm font-medium">Aggiorna</span>
          </button>
        </div>

        {/* 2. Prossimi 4 Check-in/Check-out - ARRICCHITO con info dettagliate per CHECK-OUT */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E7E5E4] p-6">
          <h2 className="font-heading text-xl font-bold text-[#78716C] text-center mb-4">
            Prossimi Check-in / Check-out
          </h2>
          
          {nextEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {nextEvents.map((event, idx) => {
                const isCheckIn = event.type === "check_in";
                const isCheckOut = event.type === "check_out";
                const hasNotes = event.notes && event.notes.trim().length > 0;
                const notesExpanded = expandedNotes[`${event.type}-${idx}`];
                const isOverbooking = event.isOverbooking;
                
                return (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow relative ${
                      isOverbooking 
                        ? "bg-red-50 border-l-red-500 ring-2 ring-red-400" 
                        : isCheckIn 
                          ? "bg-pink-50 border-l-pink-500" 
                          : "bg-violet-50 border-l-violet-500"
                    }`}
                    onClick={() => event.booking && setBookingModal({ open: true, booking: event.booking })}
                  >
                    {/* OVERBOOKING WARNING */}
                    {isOverbooking && (
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md">
                        ⚠️ OVERBOOKING
                      </div>
                    )}
                    
                    {/* Header: tipo + data */}
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold uppercase tracking-wide ${
                        isOverbooking ? "text-red-600" : isCheckIn ? "text-pink-600" : "text-violet-600"
                      }`}>
                        {isCheckIn ? "CHECK-IN" : "CHECK-OUT"}
                      </span>
                      {/* Data - font-weight 700 (font-bold) */}
                      <span className="text-sm font-bold text-[#1C1917]">
                        {formatDateLocal(event.date)}
                      </span>
                    </div>
                    
                    {/* Provenienza (nome piattaforma o ospite) */}
                    <p className="font-heading text-lg font-bold text-[#1C1917]">
                      {event.name}
                    </p>
                    
                    {/* ========== CHECK-OUT: Card semplice e operativa ========== */}
                    {isCheckOut && (
                      <div className="mt-3 pt-3 border-t border-violet-200">
                        {/* Testo principale in MAIUSCOLO - colore NERO come nome */}
                        <p className="text-sm font-bold text-[#1C1917] uppercase tracking-wide">
                          ESEGUIRE INTERVENTO DI PULIZIE
                        </p>
                        {/* Avviso - colore ROSA come CHECK-IN, SENZA icona */}
                        <p className="mt-5 text-sm text-pink-600">
                          Controllare Dettagli del prossimo Check in
                        </p>
                      </div>
                    )}
                    
                    {/* ========== CHECK-IN: Card con TUTTI i dettagli ========== */}
                    {isCheckIn && (
                      <div className="mt-3 pt-3 border-t border-pink-200 space-y-2">
                        {/* Numero notti - font-weight 700 (font-bold) */}
                        {event.nights > 0 && (
                          <div className="text-sm font-bold text-[#78716C]">
                            {event.nights} notti
                          </div>
                        )}
                        
                        {/* Numero ospiti totale */}
                        {event.guests > 0 && (
                          <div className="flex items-center gap-2 text-sm text-pink-700">
                            <Users size={14} />
                            <span className="font-medium">{event.guests} ospiti</span>
                          </div>
                        )}
                        
                        {/* Dettaglio ospiti: Adulti, Bambini con età, Neonati */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-pink-600">
                          {event.adults > 0 && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {event.adults} Adult{event.adults > 1 ? 'i' : 'o'}
                            </span>
                          )}
                          {event.children > 0 && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {event.children} Bambin{event.children > 1 ? 'i' : 'o'}
                              {event.children_ages && (
                                <span className="text-pink-500 ml-1">
                                  {formatChildrenAges(event.children_ages)}
                                </span>
                              )}
                            </span>
                          )}
                          {event.infants > 0 && (
                            <span className="flex items-center gap-1">
                              <Baby size={12} />
                              {event.infants} Neonat{event.infants > 1 ? 'i' : 'o'}
                            </span>
                          )}
                        </div>
                        
                        {/* NOTE COMPLETE - sempre visibili per CHECK-IN */}
                        {hasNotes && (
                          <div 
                            className="bg-yellow-100 border border-yellow-300 rounded-md p-2 mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedNotes(prev => ({
                                ...prev,
                                [`${event.type}-${idx}`]: !prev[`${event.type}-${idx}`]
                              }));
                            }}
                          >
                            <div className="flex items-start gap-1">
                              <FileText size={14} className="text-yellow-700 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <span className="text-xs font-semibold text-yellow-800 uppercase">Note:</span>
                                <p className="text-xs text-yellow-800 mt-0.5 whitespace-pre-wrap">
                                  {notesExpanded ? event.notes : (event.notes?.length > 150 ? `${event.notes.substring(0, 150)}...` : event.notes)}
                                </p>
                                {event.notes?.length > 150 && (
                                  <button className="text-xs text-yellow-700 underline mt-1">
                                    {notesExpanded ? "Mostra meno" : "Mostra tutto"}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Badge iCal se proveniente da piattaforma esterna */}
                        {event.source === "ical" && (
                          <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            iCal
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-[#78716C] py-8">
              Nessun check-in o check-out nei prossimi giorni
            </p>
          )}
        </div>

        {/* 3. Calendario Mensile - MIGLIORATO leggibilità */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E7E5E4] p-6">
          {/* Header navigazione */}
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={goToToday}
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Mese corrente
            </button>
            <div className="flex items-center gap-3">
              <button onClick={goToPrevMonth} className="p-1 hover:bg-[#F5F5F4] rounded">
                <ChevronLeft size={20} className="text-[#78716C]" />
              </button>
              <h2 className="font-heading text-lg md:text-xl font-bold text-[#78716C] min-w-[160px] md:min-w-[200px] text-center">
                {monthNames[calendarMonth.month]} {calendarMonth.year}
              </h2>
              <button onClick={goToNextMonth} className="p-1 hover:bg-[#F5F5F4] rounded">
                <ChevronRight size={20} className="text-[#78716C]" />
              </button>
            </div>
            <div className="w-24"></div>
          </div>

          {/* Giorni della settimana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day, i) => (
              <div key={i} className="text-xs font-medium text-[#78716C] text-center py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Griglia calendario - 3 STATI + MIGLIORATA LEGGIBILITÀ */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              if (!day.day) {
                return <div key={idx} className="aspect-square" />;
              }

              const dateStr = day.date;
              const past = isPast(dateStr);
              const today = isToday(dateStr);
              const booking = getBookingForDate(dateStr);
              const icalBlock = getIcalBlock(dateStr);
              const manualBlock = getManualBlock(dateStr);
              
              // STATI DISTINTI
              const isRealBooking = booking || icalBlock;
              const isClosed = manualBlock && !isRealBooking;
              const isOccupied = isRealBooking;
              
              // Check-out SOLO sulla data ESATTA di checkout
              // Il triangolo NON appare su celle "CHIUSO" (blocchi manuali non operativi)
              const isCheckOut = !isClosed && isCheckOutDay(dateStr);
              
              const isIcal = booking?.source?.startsWith("ical") || booking?.source === "ical";
              
              // Info display (solo per prenotazioni reali)
              const displayName = isRealBooking ? (
                booking ? (
                  isIcal 
                    ? (booking.platform_name || "Airbnb")
                    : (booking.name || booking.customer_name || "")
                ) : (icalBlock ? (icalBlock.platform_name || "Airbnb") : "")
              ) : "";
              
              const displayAmount = booking?.grand_total || booking?.base_total || 0;
              const hasNote = booking?.notes || booking?.special_requests;
              
              const bookingNights = booking && booking.check_in && booking.check_out
                ? Math.ceil((new Date(booking.check_out) - new Date(booking.check_in)) / (1000 * 60 * 60 * 24))
                : 0;
              
              // Stile cella - 3 STATI DISTINTI con MIGLIORE LEGGIBILITÀ
              let cellClass = "aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all p-1 ";
              let dayNumberClass = "";
              let eventTextClass = "";
              
              if (past) {
                cellClass += "bg-[#F5F5F4] ";
                dayNumberClass = "text-[#A8A29E]";
                eventTextClass = "text-[#A8A29E]";
              } else if (isClosed) {
                // CHIUSO: sfondo grigio SCURO, TUTTE le scritte in BIANCO
                cellClass += "bg-[#4B5563] ";
                dayNumberClass = "text-white";
                eventTextClass = "text-white";
              } else if (isOccupied) {
                // OCCUPATO: Bordeaux chiaro, testo bordeaux PIÙ SCURO per leggibilità
                cellClass += "bg-[#E8D4D4] ";
                dayNumberClass = "text-[#5C2626]"; // Bordeaux molto scuro per numeri
                eventTextClass = "text-[#6B2C2C]"; // Bordeaux scuro per testo eventi
              } else {
                // DISPONIBILE: bianco con bordo
                cellClass += "bg-white border border-[#E7E5E4] ";
                dayNumberClass = "text-[#1C1917]";
                eventTextClass = "text-[#1C1917]";
              }
              
              if (today) {
                cellClass += "ring-2 ring-[#D4AF37] ring-offset-1 ";
              }
              
              // Cliccabile solo per prenotazioni reali
              const isClickable = isRealBooking && !past;

              return (
                <div 
                  key={dateStr} 
                  className={`${cellClass} ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
                  title={isClosed ? "Chiuso" : (displayName || "Disponibile")}
                  onClick={() => {
                    if (booking && !past) {
                      setBookingModal({ open: true, booking: booking });
                    } else if (icalBlock && !past) {
                      setBookingModal({ 
                        open: true, 
                        booking: {
                          status: "ical",
                          source: icalBlock.source,
                          platform_name: icalBlock.platform_name || "Airbnb",
                          check_in: icalBlock.date,
                          check_out: null,
                          notes: icalBlock.reason || ""
                        }
                      });
                    }
                  }}
                >
                  {/* Triangolo VIOLA per check-out - SEMPRE visibile */}
                  {isCheckOut && (
                    <div 
                      className="absolute top-0 left-0 w-0 h-0 border-t-[24px] md:border-t-[28px] border-t-violet-500 border-r-[24px] md:border-r-[28px] border-r-transparent z-10"
                      title="Check-out"
                    />
                  )}
                  
                  {/* Numero giorno - FONT PIÙ LEGGIBILE */}
                  <span className={`font-bold text-base md:text-xl relative z-10 ${dayNumberClass}`}>
                    {day.day}
                  </span>
                  
                  {/* CHIUSO - Testo BIANCO per blocchi manuali su sfondo grigio scuro */}
                  {isClosed && !past && (
                    <span className="text-[9px] md:text-[10px] font-bold text-white uppercase tracking-wide">
                      CHIUSO
                    </span>
                  )}
                  
                  {/* Info prenotazione - FONT PIÙ GRANDE e PIÙ LEGGIBILE */}
                  {isOccupied && !past && (
                    <div className={`hidden md:flex flex-col items-center w-full px-0.5 relative z-10`}>
                      {/* Nome - font leggermente più grande */}
                      <span className={`text-[9px] leading-tight truncate w-full text-center font-semibold ${eventTextClass}`}>
                        {displayName.split(' ')[0]?.substring(0, 8) || "Airbnb"}
                      </span>
                      {/* Notti */}
                      {booking && bookingNights > 0 && (
                        <span className={`text-[8px] leading-tight ${eventTextClass} opacity-80`}>
                          ({bookingNights})
                        </span>
                      )}
                      {/* Importo */}
                      {displayAmount > 0 && (
                        <span className={`text-[8px] leading-tight ${eventTextClass} opacity-80`}>
                          €{displayAmount.toFixed(0)}
                        </span>
                      )}
                      {/* Indicatore note - PIÙ GRANDE e VISIBILE */}
                      {hasNote && (
                        <FileText size={12} className="text-yellow-700 mt-0.5" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legenda aggiornata - 4 stati */}
          <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-[#E7E5E4] text-xs text-[#78716C]">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-[#E8D4D4]"></span> Occupato
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-[#4B5563] flex items-center justify-center text-[5px] font-bold text-white">C</span> Chiuso
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-white border border-[#E7E5E4]"></span> Disponibile
            </span>
            <span className="flex items-center gap-1">
              <span className="w-0 h-0 border-t-[10px] md:border-t-[12px] border-t-violet-600 border-r-[10px] md:border-r-[12px] border-r-transparent"></span> Check-out
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded ring-2 ring-[#D4AF37] ring-offset-1 bg-white"></span> Oggi
            </span>
          </div>
        </div>

        {/* Footer info */}
        <div className="text-center text-xs text-[#A8A29E]">
          <p>Vista read-only • Aggiornamento automatico ogni 60 secondi</p>
        </div>
      </main>

      {/* Modal Dettagli Prenotazione (READ-ONLY) */}
      {bookingModal.open && bookingModal.booking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBookingModal({ open: false, booking: null })}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">Dettagli Prenotazione</h3>
              <button onClick={() => setBookingModal({ open: false, booking: null })} className="text-[#78716C] hover:text-[#1C1917]">
                <X size={20} />
              </button>
            </div>
            
            {(() => {
              const b = bookingModal.booking;
              const isIcal = b.status === "ical" || b.source?.startsWith("ical_") || b.source === "ical";
              const nights = b.check_in && b.check_out 
                ? Math.ceil((new Date(b.check_out) - new Date(b.check_in)) / (1000 * 60 * 60 * 24))
                : 0;
              
              return (
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      b.status === 'paid' ? 'bg-green-100 text-green-800' :
                      b.status === 'confirmed' ? 'bg-cyan-100 text-cyan-800' :
                      isIcal ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {b.status === 'paid' ? 'Pagato' :
                       b.status === 'confirmed' ? 'Confermato' :
                       isIcal ? 'iCal Import' :
                       b.status || 'Pending'}
                    </span>
                    {b.booking_number && (
                      <span className="text-sm text-[#78716C]">#{b.booking_number}</span>
                    )}
                  </div>
                  
                  {/* Nome Ospite / Piattaforma */}
                  <div>
                    <p className="text-xs text-[#78716C] uppercase tracking-wide">
                      {isIcal ? 'Piattaforma' : 'Nome Ospite'}
                    </p>
                    <p className="font-heading text-lg font-semibold">
                      {isIcal ? (b.platform_name || 'Airbnb') : (b.name || b.customer_name || 'N/A')}
                    </p>
                  </div>
                  
                  {/* Ospiti con dettaglio */}
                  {!isIcal && (b.adults || b.children || b.infants) && (
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Numero Ospiti</p>
                      <p className="font-medium">
                        {b.adults > 0 && <span>{b.adults} Adult{b.adults > 1 ? 'i' : 'o'}</span>}
                        {b.children > 0 && (
                          <span className="ml-2">
                            {b.children} Bambin{b.children > 1 ? 'i' : 'o'}
                            {b.children_ages && (
                              <span className="text-[#78716C] ml-1">
                                {formatChildrenAges(b.children_ages)}
                              </span>
                            )}
                          </span>
                        )}
                        {b.infants > 0 && <span className="ml-2">{b.infants} Neonat{b.infants > 1 ? 'i' : 'o'}</span>}
                      </p>
                    </div>
                  )}
                  
                  {/* Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Check-in</p>
                      <p className="font-medium" style={{ color: 'rgb(233, 81, 145)' }}>
                        {b.check_in ? formatDisplayDate(b.check_in) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Check-out</p>
                      <p className="font-medium" style={{ color: 'rgb(124, 58, 237)' }}>
                        {b.check_out ? formatDisplayDate(b.check_out) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Notti */}
                  {nights > 0 && (
                    <div className="bg-[#F9F8F6] p-3 rounded-lg flex items-center justify-center gap-2">
                      <Calendar size={18} className="text-[#D4AF37]" />
                      <span className="font-medium">{nights} nott{nights === 1 ? 'e' : 'i'}</span>
                    </div>
                  )}
                  
                  {/* Note - FONDAMENTALI per il team pulizie */}
                  {(b.notes || b.special_requests) && (
                    <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3 rounded-lg">
                      <p className="text-xs text-[#92400E] uppercase tracking-wide mb-1 flex items-center gap-1">
                        <FileText size={14} /> Note
                      </p>
                      <p className="text-sm text-[#92400E] whitespace-pre-wrap">
                        {b.notes || b.special_requests}
                      </p>
                    </div>
                  )}
                  
                  {/* Fonte */}
                  <div className="text-xs text-[#A8A29E] text-center pt-2 border-t border-[#E7E5E4]">
                    Fonte: {b.source || 'website'}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
